import React, { useEffect, useMemo } from "react";

const killHashIfView = () => {
  const raw = (window.location.hash || "").replace("#", "").trim().toLowerCase();
  if (!raw) return;

  const allowed = ["dashboard", "clients", "case_detail", "documents", "settings", "account", "login"];
  if (!allowed.includes(raw)) return;

  window.history.replaceState({}, "", window.location.pathname + window.location.search);
};

const resolveTenantIdFast = (urlParams: URLSearchParams): string | null => {
  const seg = window.location.pathname.split("/").filter(Boolean);
  return (
    (seg[0] === "location" ? seg[1] : null) ||
    urlParams.get("locationId") ||
    urlParams.get("staff_context") ||
    sessionStorage.getItem("FILERSHUB_SSO_LOC")
  );
};

const GHLOAuthCallback: React.FC = () => {
  const urlParams = useMemo(() => new URLSearchParams(window.location.search), []);
  const connectedId = urlParams.get("crm_connected") || urlParams.get("ghl_connected");

  useEffect(() => {
    // Prevent hash-based routing from sticking during/after callback
    killHashIfView();

    if (!connectedId) return;

    const locId = resolveTenantIdFast(urlParams);

    // Build next URL: always tenant-scoped settings
    const next = new URL(window.location.origin + (locId ? `/location/${locId}/settings` : `/`));

    // Preserve existing query params (minus old connection markers)
    urlParams.forEach((value, key) => {
      if (key === "crm_connected" || key === "ghl_connected") return;
      next.searchParams.set(key, value);
    });

    // Use a single neutral marker. Keep it stable for your app.
    next.searchParams.set("crm_connected", connectedId);

    // Cache tenant for future loads
    if (locId) sessionStorage.setItem("FILERSHUB_SSO_LOC", locId);

    // Small delay so users see the success UI, but don’t sit there spinning forever
    const t = window.setTimeout(() => {
      window.location.replace(next.pathname + next.search);
      window.dispatchEvent(new Event("filershub:navigate"));
    }, 350);

    return () => window.clearTimeout(t);
  }, [connectedId, urlParams]);

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-3xl shadow-2xl p-10 max-w-md w-full text-center border border-slate-100">
        {!connectedId ? (
          <div className="space-y-6">
            <div className="w-16 h-16 border-4 border-brand-100 border-t-brand-600 rounded-full animate-spin mx-auto"></div>
            <h2 className="text-xl font-bold text-slate-800">Authorizing Integration...</h2>
            <p className="text-slate-500 text-sm">
              We are establishing a secure connection to your account. Please do not close this window.
            </p>
          </div>
        ) : (
          <div className="space-y-6 animate-fade-in-up">
            <div className="w-20 h-20 bg-emerald-50 text-emerald-600 rounded-full flex items-center justify-center mx-auto text-4xl shadow-inner">
              ✅
            </div>
            <h2 className="text-2xl font-black text-slate-800">Connection Verified</h2>
            <p className="text-slate-500 text-sm font-medium">
              Your account has been synced. Returning you to settings now...
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default GHLOAuthCallback;
